import Head from 'next/head';
import { NextSeo } from 'next-seo';
import TestSeriesPageContent from '@cont/TestSeries/TestSeriesPageContent';
import TestSeriesFlexLayout from '@layout/TestSeriesFlexLayout/TestSeriesFlexLayout';
import { TestSeriesDataProvider } from '@cont/TestSeries/useTestSeriesData';
import fetchUaits from '@api/fetchers/uaits';

const TestSeriesPage = ({ data }) => {
  if (!data) {
    return (
      <>
        <NextSeo
          title="Test Series"
          description="Test Series on Unacademy"
          canonical="https://unacademy.com/testseries"
        />
        <Head>
          <title>Test Series</title>
        </Head>
        <TestSeriesDataProvider
          initialData={{
            id: 0,
            eventId: '',
            heroSection: {
              mainHeadline: '',
              supportingText: '',
              ctaButtonLabel: '',
              tagline: ''
            },
            statsCard: [],
            statsSectionTitle: '',
            featureCard: [],
            helpSection: {
              title: '',
              description: '',
              ctaText: '',
              contact: ''
            },
            faqCard: [],
            testSeriesSectionTitle: '',
            customSkuCard: [],
            hybridSkuCard: [],
            faqSectionTitle: '',
            faqSectionSubtitle: ''
          }}
        >
          <TestSeriesPageContent />
        </TestSeriesDataProvider>
      </>
    );
  }

  const seoTitle = data?.heroSection?.mainHeadline || 'Test Series';
  const seoDescription =
    data?.heroSection?.supportingText || 'Test Series on Unacademy';
  const canonical = `https://unacademy.com/testseries/${data?.eventId}`;

  return (
    <>
      <NextSeo
        title={seoTitle}
        description={seoDescription}
        canonical={canonical}
      />
      <Head>
        <title>{seoTitle}</title>
      </Head>
      <TestSeriesDataProvider initialData={data}>
        <TestSeriesPageContent />
      </TestSeriesDataProvider>
    </>
  );
};

TestSeriesPage.pageInfo = {
  isMWebSupported: true,
  name: 'Test Series Page',
  layoutOptions: {
    hideIntercom: true,
    hideFreshbot: true,
    disableDarkMode: true,
    showLogin: true
  }
};

const parseCsvLine = (line) => {
  const result = [];
  let current = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i += 1) {
    const char = line[i];

    if (inQuotes) {
      if (char === '"') {
        if (i + 1 < line.length && line[i + 1] === '"') {
          current += '"';
          i += 1;
        } else {
          inQuotes = false;
        }
      } else {
        current += char;
      }
    } else if (char === '"') {
      inQuotes = true;
    } else if (char === ',') {
      result.push(current);
      current = '';
    } else {
      current += char;
    }
  }
  result.push(current);
  return result;
};

// ✅ SSR:
export const getServerSideProps = async ({ params }) => {
  const { eventUid } = params;
  if (!eventUid) {
    return {
      notFound: true
    };
  }

  try {
    const res = await fetchUaits(eventUid);

    if (!res || !res.hybridSkuCard || !Array.isArray(res.hybridSkuCard)) {
      return {
        props: {
          eventUid,
          data: res || {}
        }
      };
    }

    const enrichedHybridCards = await Promise.all(
      res.hybridSkuCard.map(async (card) => {
        const addressCsv = card.hybridDetailsCsv?.addressCsv?.url;
        if (!card || !addressCsv) {
          return { ...card, csvDetails: [] };
        }

        try {
          const response = await fetch(addressCsv);
          const csvText = await response.text();

          if (!csvText || !csvText.trim()) {
            return { ...card, csvDetails: [] };
          }

          const lines = csvText.trim().split(/\r?\n/);

          if (!lines || lines.length < 1) {
            return { ...card, csvDetails: [] };
          }

          const headers = parseCsvLine(lines[0]);

          if (!headers || headers.length === 0) {
            return { ...card, csvDetails: [] };
          }

          const csvData = lines
            .slice(1)
            .map((line) => {
              if (!line) return null;
              const values = parseCsvLine(line);

              if (values.length !== headers.length) {
                return null;
              }

              const baseObject = Object.fromEntries(
                headers.map((h, i) => [h.trim(), values[i]?.trim() || ''])
              );

              return baseObject;
            })
            .filter(Boolean);

          return { ...card, csvDetails: csvData };
        } catch (err) {
          return { ...card, csvDetails: [] };
        }
      })
    );

    const enrichedData = {
      ...res,
      hybridSkuCard: enrichedHybridCards
    };

    return {
      props: {
        eventUid,
        data: enrichedData
      }
    };
  } catch (error) {
    return {
      notFound: true
    };
  }
};

export default TestSeriesPage;

TestSeriesPage.Layout = TestSeriesFlexLayout;
